/**
 * 채팅 웹소켓 훅
 *
 * - useWebSocket: 연결·재연결(지수 백오프)·하트비트(ping/pong 데드라인)·명시적 종료를 책임지는 기반 훅
 * - useChatWebSocket: 수신 프레임을 React Query 캐시(["chat-messages", roomId] / ["chat-rooms"])에 반영하고
 *   연결 상태·타이핑·읽음·전송 함수·원시 프레임 구독을 컨텍스트 값으로 제공
 *
 * 2026-10-03 개편 요지 (메신저 감사):
 * - 메시지는 캐시가 단일 진실. 평면 messages 배열·addMessage/clearMessages/deletedMessageIds 병행 저장소 폐기
 *   (방 목록이 길이 차이로 엉뚱한 방에 안읽음을 붙이던 원인).
 * - 재연결 시 방 목록·열린 방 메시지를 다시 받아 끊긴 사이 놓친 메시지를 보정.
 * - pong 데드라인: 응답 없는 반쯤 열린 소켓을 끊고 재연결 (네트워크 전환·절전 복귀 후 '연결됨'인데 수신 없던 문제).
 * - disconnect() 는 onclose 를 떼고 닫는다 (Provider 언마운트 뒤 자동 재연결로 좀비 소켓이 남던 문제).
 * - 보고 있는 방(join)은 소켓이 열릴 때마다 다시 알린다 (열리기 전에 보낸 join 이 버려지던 문제).
 * - 'read' 프레임은 본인의 다른 창에서도 와서 방 목록 안읽음을 0 으로 맞춘다.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { useUIStore } from "@/stores/uiStore";
import { getAccessToken, getTokenExpiry, refreshSession } from "@/services/api";
import type {
  ChatErrorPayload,
  Message,
  MessageType,
  OnlinePayload,
  ReadReceiptPayload,
  ReadReceiptsMap,
  TypingPayload,
  WebSocketMessage,
} from "@/types/chat";
import type { Notification } from "@/types/notification";
import type { ChatWebSocketContextValue } from "@/contexts/ChatWebSocketContext";
import { toast } from "@/hooks/use-toast";
import { messagePreviewText } from "@/components/chat/chatStickers";
import { isChatPopupFocused, openChatPopupIfNeeded } from "@/lib/chatPopup";
import {
  CHAT_ROOMS_KEY,
  applyIncomingMessageToRooms,
  resetCachedRoomUnread,
  setCachedMemberRead,
  updateCachedChatMessages,
  upsertCachedChatMessage,
} from "@/lib/chatMessagesCache";

export type { ReadReceiptsMap } from "@/types/chat";

// ============================================
// Base WebSocket Hook
// ============================================

interface WebSocketOptions {
  url: string;
  onMessage?: (data: unknown) => void;
  /** isReconnect: 이전에 한 번이라도 열렸던 소켓이 다시 열림 (끊긴 사이 놓친 데이터 보정용) */
  onOpen?: (isReconnect: boolean) => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  /** ping 주기(ms). 0 이면 하트비트 없음 */
  heartbeatIntervalMs?: number;
  /** 마지막 pong 이후 이 시간(ms) 동안 응답이 없으면 소켓을 끊고 재연결 */
  heartbeatTimeoutMs?: number;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  /** 소켓이 열려 있으면 보내고 true */
  send: (data: unknown) => boolean;
  reconnect: () => void;
  disconnect: () => void;
}

export function useWebSocket({
  url,
  onMessage,
  onOpen,
  onClose,
  onError,
  reconnectInterval = 1000,
  maxReconnectAttempts = 20,
  heartbeatIntervalMs = 15000,
  heartbeatTimeoutMs = 40000,
}: WebSocketOptions): UseWebSocketReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const connectingRef = useRef(false);
  const hadOpenRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval>>();
  const lastPongRef = useRef(0);
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated } = useAuthStore();

  // 최신 콜백을 재연결 없이 쓰기 위한 ref
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = undefined;
    }
  }, []);

  const connectRef = useRef<() => Promise<void>>(async () => {});

  const scheduleReconnect = useCallback(() => {
    if (!isAuthenticated || reconnectAttemptsRef.current >= maxReconnectAttempts) return;
    if (reconnectTimeoutRef.current) return;
    const backoffDelay = Math.min(reconnectInterval * Math.pow(2, reconnectAttemptsRef.current), 30000);
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = undefined;
      reconnectAttemptsRef.current += 1;
      void connectRef.current();
    }, backoffDelay);
  }, [isAuthenticated, maxReconnectAttempts, reconnectInterval]);

  const connect = useCallback(async () => {
    if (!isAuthenticated || !url) return;
    // 동시 connect 방지 (재연결 타이머 + online 이벤트 + 포커스 복귀가 겹치면 소켓이 둘 생겨 프레임이 중복 수신됐다)
    if (connectingRef.current) return;
    const current = wsRef.current;
    if (current && (current.readyState === WebSocket.CONNECTING || current.readyState === WebSocket.OPEN)) return;

    let token = getAccessToken();
    if (!token) return;

    connectingRef.current = true;
    try {
      // 토큰 만료 임박 → 갱신 후 연결. 인터셉터·타이머와 같은 갱신 Promise 를 공유한다
      const expiry = getTokenExpiry(token);
      if (expiry && expiry - Date.now() < 30000) {
        try {
          const result = await refreshSession();
          if (!result) return; // 세션이 정리됨 — 다음 connect 는 토큰 없음으로 종료
          token = result.access_token;
        } catch {
          scheduleReconnect(); // 일시 실패(네트워크 등)
          return;
        }
      }

      const ws = new WebSocket(`${url}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        if (wsRef.current !== ws) return;
        const isReconnect = hadOpenRef.current;
        hadOpenRef.current = true;
        reconnectAttemptsRef.current = 0;
        lastPongRef.current = Date.now();
        setIsConnected(true);

        if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
        if (heartbeatIntervalMs > 0) {
          heartbeatTimerRef.current = setInterval(() => {
            if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;
            if (Date.now() - lastPongRef.current > heartbeatTimeoutMs) {
              // 반쯤 열린 소켓: 서버 응답이 없다 → 끊고 onclose 경로로 재연결
              ws.close();
              return;
            }
            ws.send(JSON.stringify({ type: "ping" }));
          }, heartbeatIntervalMs);
        }

        onOpenRef.current?.(isReconnect);
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws) return;
        let data: unknown = event.data;
        try {
          data = JSON.parse(event.data);
        } catch {
          // 비 JSON 프레임은 그대로 전달
        }
        if ((data as { type?: string } | null)?.type === "pong") lastPongRef.current = Date.now();
        onMessageRef.current?.(data);
      };

      ws.onclose = () => {
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (heartbeatTimerRef.current) {
          clearInterval(heartbeatTimerRef.current);
          heartbeatTimerRef.current = undefined;
        }
        setIsConnected(false);
        onCloseRef.current?.();
        scheduleReconnect();
      };

      ws.onerror = (error) => {
        if (wsRef.current !== ws) return;
        onErrorRef.current?.(error);
      };
    } finally {
      connectingRef.current = false;
    }
  }, [url, isAuthenticated, heartbeatIntervalMs, heartbeatTimeoutMs, scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    clearTimers();
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      // onclose 를 떼고 닫는다 — 안 그러면 자동 재연결이 돌아 언마운트된 Provider 의 좀비 소켓이 남는다
      ws.onclose = null;
      ws.onmessage = null;
      ws.onerror = null;
      try {
        ws.close();
      } catch {
        // 이미 닫힘
      }
    }
    setIsConnected(false);
  }, [clearTimers]);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    void connect();
  }, [disconnect, connect]);

  const send = useCallback((data: unknown): boolean => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    void connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // 네트워크 복귀·탭 복귀 시 백오프를 기다리지 않고 즉시 재연결
  useEffect(() => {
    const kick = () => {
      if (!isAuthenticated) return;
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) return;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }
      reconnectAttemptsRef.current = 0;
      void connect();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") kick();
    };
    window.addEventListener("online", kick);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", kick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [connect, isAuthenticated]);

  return { isConnected, send, reconnect, disconnect };
}

// ============================================
// Chat WebSocket Hook
// ============================================

type IncomingMessage = Message & {
  user_name?: string | null;
  user?: { id: string; name: string; profile_image?: string | null } | null;
  /** 보낸 창의 전송 중(pending) 메시지 확정용 — 서버가 보낸 사람 payload 의 temp_id 를 그대로 에코 */
  client_temp_id?: string;
};

// Helper function to show browser notification
const showBrowserNotification = (title: string, body: string, roomId?: number) => {
  if ("Notification" in window && Notification.permission === "granted") {
    const notification = new window.Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: `chat-notification-${roomId || "general"}`,
    });

    notification.onclick = () => {
      window.focus();
      if (roomId) {
        openChatPopupIfNeeded(roomId);
      }
      notification.close();
    };

    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if ("Notification" in window && Notification.permission === "default") {
    await Notification.requestPermission();
  }
};

function buildChatWsUrl(): string {
  if (typeof window === "undefined") return "";

  const hostname = window.location.hostname;

  if (hostname === "ione.dev-one.co.kr" || hostname === "i1.dev-one.co.kr") {
    return `wss://${hostname}/ws/chat`;
  }

  // 개발 모드는 어떤 호스트(LAN IP 포함)로 접속하든 Vite 프록시(/ws → backend)를 탄다
  if (import.meta.env.DEV || hostname === "localhost") {
    return `ws://${window.location.host}/ws/chat`;
  }

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  let host = window.location.host;

  // VITE_API_URL이 설정되어 있으면 해당 host 사용
  if (import.meta.env.VITE_API_URL) {
    try {
      host = new URL(import.meta.env.VITE_API_URL).host;
    } catch {
      console.warn("Invalid VITE_API_URL, using window.location.host");
    }
  }

  return `${protocol}//${host}/ws/chat`;
}

export function useChatWebSocket(): ChatWebSocketContextValue {
  const queryClient = useQueryClient();
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [typingUsers, setTypingUsers] = useState<Map<number, Set<string>>>(new Map());
  const [readReceipts, setReadReceipts] = useState<ReadReceiptsMap>(new Map());
  const { addNotification, setUnreadCount } = useNotificationStore();
  const isPopupWindow = typeof window !== "undefined" && window.location.pathname.includes("/chat/popup/");
  // 이 사용자의 데스크톱 메신저(HubCoreMessenger)가 켜져 있으면 브라우저 채팅 알림·자동 팝업은 중복이라 억제
  // (업무 알림은 메신저와 무관하므로 그대로 표시). 서버가 online_list / desktop_presence 로 알려준다.
  const desktopOnlineRef = useRef(false);
  // 지금 보고 있는 방 — 소켓이 열릴 때마다 서버에 다시 알린다
  const viewingRoomRef = useRef<number | null>(null);
  const listenersRef = useRef(new Set<(frame: WebSocketMessage) => void>());
  const sendRef = useRef<(data: unknown) => boolean>(() => false);

  const wsUrl = useMemo(buildChatWsUrl, []);

  const isViewingRoom = useCallback((roomId: number) => {
    if (viewingRoomRef.current === roomId && document.visibilityState === "visible" && document.hasFocus()) {
      return true;
    }
    // 메인 창: 이 창이 띄운 해당 방 팝업이 포커스를 갖고 있으면 '보고 있는 중' 으로 본다
    return isChatPopupFocused(roomId);
  }, []);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback(
    (data: unknown) => {
      const frame = data as WebSocketMessage;
      if (!frame || typeof frame !== "object" || !("type" in frame)) return;
      const me = useAuthStore.getState().user;

      switch (frame.type) {
        case "message": {
          const message = frame.payload as IncomingMessage;
          const roomId = message.room_id;
          const isOwn = !!me && message.user_id === me.id;
          const viewing = isViewingRoom(roomId);

          upsertCachedChatMessage(queryClient, message);
          const inList = applyIncomingMessageToRooms(queryClient, message, {
            countUnread: !isOwn && !viewing,
          });
          if (!inList) {
            // 목록에 없는 방(새로 초대됨 등) — 목록을 다시 받는다
            void queryClient.invalidateQueries({ queryKey: CHAT_ROOMS_KEY });
          }

          // 알림 및 자동 팝업 (데스크톱 메신저가 켜져 있으면 그쪽이 알리므로 생략)
          const { isDnd } = useNotificationStore.getState();
          const chatAutoPopup = useUIStore.getState().chatAutoPopup;
          if (!isOwn && !viewing && message.message_type !== "system" && !isDnd && !desktopOnlineRef.current) {
            const senderName = message.user_name || message.user?.name || "새 메시지";
            const preview = messagePreviewText(message.message_type, message.content);

            if (chatAutoPopup && !isPopupWindow) {
              // 이미 열린 팝업은 다시 띄우지 않는다 (같은 URL 재탐색으로 입력 중 내용이 날아가던 문제)
              const popup = openChatPopupIfNeeded(roomId);
              if (!popup) {
                // 팝업이 차단된 경우 브라우저 알림으로 대체
                showBrowserNotification(senderName, preview, roomId);
              }
            } else if (document.hidden) {
              showBrowserNotification(senderName, preview, roomId);
            }
          }
          break;
        }

        case "online_list": {
          const payload = frame.payload as { users: string[]; desktop_online?: boolean };
          setOnlineUsers(payload.users ?? []);
          desktopOnlineRef.current = payload.desktop_online === true;
          break;
        }

        case "desktop_presence": {
          const payload = frame.payload as { online: boolean };
          desktopOnlineRef.current = payload.online === true;
          break;
        }

        case "online": {
          const payload = frame.payload as OnlinePayload;
          setOnlineUsers((prev) =>
            payload.is_online ? [...new Set([...prev, payload.user_id])] : prev.filter((id) => id !== payload.user_id)
          );
          break;
        }

        case "typing": {
          const payload = frame.payload as TypingPayload;
          if (me && payload.user_id === me.id) break;
          setTypingUsers((prev) => {
            const newMap = new Map(prev);
            const roomTyping = new Set(newMap.get(payload.room_id) ?? []);
            if (payload.is_typing) roomTyping.add(payload.user_id);
            else roomTyping.delete(payload.user_id);
            newMap.set(payload.room_id, roomTyping);
            return newMap;
          });
          break;
        }

        case "notification": {
          const notificationPayload = frame.payload as Notification & { email_fallback?: boolean };
          addNotification(notificationPayload);

          // silent=true (DND 모드) 시 브라우저 알림 차단
          if (frame.silent !== true && document.hidden) {
            showBrowserNotification(notificationPayload.title, notificationPayload.content || "");
          }

          // 푸시 구독 미등록으로 이메일 폴백 발송된 경우 안내
          if (notificationPayload.email_fallback) {
            toast({
              title: "이메일로 알림 발송",
              description:
                "브라우저 푸시 알림이 등록되어 있지 않아 이메일로 대신 발송되었습니다. 설정에서 푸시 알림을 허용하시면 더 빠르게 알림을 받을 수 있습니다.",
            });
          }
          break;
        }

        case "notification_count": {
          const countPayload = frame.payload as { count: number };
          setUnreadCount(countPayload.count);
          break;
        }

        case "read": {
          const payload = frame.payload as ReadReceiptPayload;
          setReadReceipts((prev) => {
            const newMap = new Map(prev);
            const roomReceipts = new Map(newMap.get(payload.room_id) ?? []);
            const current = roomReceipts.get(payload.user_id) ?? 0;
            if (payload.last_read_message_id <= current) return prev;
            roomReceipts.set(payload.user_id, payload.last_read_message_id);
            newMap.set(payload.room_id, roomReceipts);
            return newMap;
          });
          if (me && payload.user_id === me.id) {
            // 내가 다른 창/앱에서 읽음 → 이 창의 방 목록 안읽음도 0
            resetCachedRoomUnread(queryClient, payload.room_id, me.id, payload.last_read_message_id);
          } else {
            setCachedMemberRead(queryClient, payload.room_id, payload.user_id, payload.last_read_message_id);
          }
          break;
        }

        case "message_deleted": {
          const payload = frame.payload as { message_id: number; room_id: number };
          updateCachedChatMessages<Message>(queryClient, payload.room_id, (m) =>
            m.id === payload.message_id && !m.is_deleted ? { ...m, is_deleted: true } : m
          );
          break;
        }

        case "reaction_added": {
          const payload = frame.payload as {
            message_id: number;
            room_id: number;
            reaction: { id: number; message_id: number; user_id: string; emoji: string; created_at: string };
          };
          updateCachedChatMessages<Message>(queryClient, payload.room_id, (msg) => {
            if (msg.id !== payload.message_id) return msg;
            const currentReactions = msg.reactions || [];
            if (currentReactions.some((r) => r.id === payload.reaction.id)) return msg;
            return { ...msg, reactions: [...currentReactions, payload.reaction] };
          });
          break;
        }

        case "reaction_removed": {
          const payload = frame.payload as {
            message_id: number;
            room_id: number;
            reaction_id: number;
            user_id: string;
            emoji: string;
          };
          updateCachedChatMessages<Message>(queryClient, payload.room_id, (msg) => {
            if (msg.id !== payload.message_id) return msg;
            const currentReactions = msg.reactions || [];
            const nextReactions = currentReactions.filter((r) => r.id !== payload.reaction_id);
            if (nextReactions.length === currentReactions.length) return msg;
            return { ...msg, reactions: nextReactions };
          });
          break;
        }

        case "reservation_updated": {
          // 회의실 예약은 여러 사람이 동시에 보는 공용 일정판이라,
          // 다른 사람이 예약/변경/취소하면 새로고침 없이 화면이 갱신되어야 한다.
          void queryClient.invalidateQueries({ queryKey: ["reservations"] });
          break;
        }

        case "poll_updated":
        case "poll_deleted": {
          const payload = frame.payload as { poll_id: number; room_id: number };
          // 투표 카드 집계(extra_data)는 서버 계산값이라 해당 방 메시지를 다시 받는다 (열려 있는 쿼리만)
          void queryClient.invalidateQueries({ queryKey: ["chat-messages", payload.room_id], refetchType: "active" });
          void queryClient.invalidateQueries({ queryKey: ["poll", payload.poll_id] });
          break;
        }

        case "error": {
          const payload = frame.payload as ChatErrorPayload;
          toast({ title: "전송할 수 없습니다", description: payload.message, variant: "destructive" });
          break;
        }

        default:
          break;
      }

      // 원시 프레임 구독자 (전송 중 메시지 확정 등) — 캐시 반영 이후에 알린다
      for (const listener of listenersRef.current) {
        try {
          listener(frame);
        } catch (e) {
          console.error("[chat-ws] listener error", e);
        }
      }
    },
    [addNotification, setUnreadCount, queryClient, isPopupWindow, isViewingRoom]
  );

  const handleOpen = useCallback(
    (isReconnect: boolean) => {
      // 보고 있는 방을 서버에 (다시) 알린다 — 열리기 전에 보낸 join 은 버려지므로 여기서 보장
      if (viewingRoomRef.current !== null && document.visibilityState === "visible") {
        sendRef.current({ type: "join", payload: { room_id: viewingRoomRef.current } });
      }
      if (isReconnect) {
        // 끊긴 사이 놓친 메시지·안읽음 보정: 방 목록 + 열려 있는 방의 메시지
        void queryClient.invalidateQueries({ queryKey: CHAT_ROOMS_KEY });
        void queryClient.invalidateQueries({ queryKey: ["chat-messages"], refetchType: "active" });
      }
    },
    [queryClient]
  );

  const { isConnected, send, reconnect, disconnect } = useWebSocket({
    url: wsUrl,
    onMessage: handleMessage,
    onOpen: handleOpen,
  });

  useEffect(() => {
    sendRef.current = send;
  }, [send]);

  // 창이 가려지면 열람 해제(이 방의 푸시가 다시 온다), 돌아오면 다시 등록
  useEffect(() => {
    const onVisibility = () => {
      const roomId = viewingRoomRef.current;
      if (roomId === null) return;
      if (document.visibilityState === "visible") {
        sendRef.current({ type: "join", payload: { room_id: roomId } });
      } else {
        sendRef.current({ type: "leave", payload: { room_id: roomId } });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const sendMessage = useCallback(
    (roomId: number, content: string, replyToId?: number, messageType: MessageType = "text", tempId?: string) =>
      send({
        type: "message",
        payload: {
          room_id: roomId,
          content,
          message_type: messageType,
          reply_to_id: replyToId,
          temp_id: tempId,
        },
      }),
    [send]
  );

  const sendTyping = useCallback(
    (roomId: number, isTyping: boolean) => {
      send({ type: "typing", payload: { room_id: roomId, is_typing: isTyping } });
    },
    [send]
  );

  const sendReadReceipt = useCallback(
    (roomId: number, messageId: number) => {
      send({ type: "read", payload: { room_id: roomId, message_id: messageId } });
      // 서버 'read' 브로드캐스트는 본인에게도 오지만, 즉시 반영을 위해 로컬도 갱신
      const me = useAuthStore.getState().user;
      if (me?.id) {
        setReadReceipts((prev) => {
          const roomReceipts = prev.get(roomId);
          const current = roomReceipts?.get(me.id);
          if (current !== undefined && current >= messageId) return prev;
          const newMap = new Map(prev);
          const next = new Map(roomReceipts ?? []);
          next.set(me.id, messageId);
          newMap.set(roomId, next);
          return newMap;
        });
        resetCachedRoomUnread(queryClient, roomId, me.id, messageId);
      }
    },
    [send, queryClient]
  );

  const setViewingRoom = useCallback(
    (roomId: number | null) => {
      const prev = viewingRoomRef.current;
      if (prev === roomId) return;
      if (prev !== null) send({ type: "leave", payload: { room_id: prev } });
      viewingRoomRef.current = roomId;
      if (roomId !== null && document.visibilityState === "visible") {
        send({ type: "join", payload: { room_id: roomId } });
      }
    },
    [send]
  );

  const addMessage = useCallback(
    (message: Message & { user_name?: string | null }) => {
      upsertCachedChatMessage(queryClient, message);
      applyIncomingMessageToRooms(queryClient, message, { countUnread: false });
    },
    [queryClient]
  );

  // Initialize read receipts from room member data (서버값과 실시간 수신값 중 큰 쪽 유지)
  const initializeReadReceipts = useCallback(
    (roomId: number, memberReceipts: { userId: string; lastReadMessageId: number }[]) => {
      setReadReceipts((prev) => {
        const newMap = new Map(prev);
        const roomReceipts = new Map(newMap.get(roomId) ?? []);
        for (const receipt of memberReceipts) {
          if (!receipt.lastReadMessageId) continue;
          const current = roomReceipts.get(receipt.userId) ?? 0;
          if (receipt.lastReadMessageId > current) roomReceipts.set(receipt.userId, receipt.lastReadMessageId);
        }
        newMap.set(roomId, roomReceipts);
        return newMap;
      });
    },
    []
  );

  const subscribe = useCallback((listener: (frame: WebSocketMessage) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  return {
    isConnected,
    onlineUsers,
    typingUsers,
    readReceipts,
    sendMessage,
    sendTyping,
    sendReadReceipt,
    setViewingRoom,
    reconnect,
    disconnect,
    addMessage,
    initializeReadReceipts,
    subscribe,
  };
}
